import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API, useAuth } from "../App";
import QMSLayout from "../components/QMSLayout";
import axios from "axios";
import {
  BarChart, Bar, PieChart, Pie, Cell, RadialBarChart, RadialBar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Shield, AlertTriangle, Loader2, RefreshCw, Activity,
  Building2, Eye, FileText, Package, Zap, Target, ArrowUpRight,
  ArrowDownRight, Minus, Heart, Server, Clock, CheckCircle2
} from "lucide-react";

const ax = axios.create({ withCredentials: true });

const POSTURE_COLORS = { strong: "#22C55E", moderate: "#F59E0B", at_risk: "#F97316", critical: "#EF4444" };
const POSTURE_LABELS = { strong: "Strong", moderate: "Moderate", at_risk: "At Risk", critical: "Critical" };
const SEVERITY_COLORS = { critical: "#EF4444", high: "#F97316", medium: "#FBBF24", low: "#22C55E" };
const STATUS_COLORS = { approved: "#22C55E", rejected: "#EF4444", submitted: "#60A5FA", under_review: "#FBBF24", draft: "#6B7280", inactive: "#9CA3AF", pending: "#F59E0B", completed: "#22C55E", open: "#EF4444", acknowledged: "#FBBF24", dismissed: "#6B7280" };

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1F222D] border border-[#2A2D38] rounded-lg p-2.5 shadow-xl">
      <p className="text-[10px] text-[#8B95A5] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs" style={{ color: p.color || p.fill }}>
          {p.name}: <span className="font-semibold text-white">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const MiniCard = ({ label, value, icon: Icon, color, sub }) => (
  <div className="bg-[#181B23] border border-[#2A2D38] rounded-lg p-3" data-testid={`mini-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-2">
      <Icon className={`w-4 h-4 ${color} flex-shrink-0`} />
      <div className="min-w-0">
        <div className="text-lg font-bold text-white leading-tight">{value ?? "—"}</div>
        <div className="text-[10px] text-[#8B95A5] truncate">{label}</div>
        {sub && <div className="text-[10px] text-[#8B95A5]">{sub}</div>}
      </div>
    </div>
  </div>
);

const Section = ({ title, icon: Icon, color, children, id }) => (
  <div className="bg-[#1F222D] border border-[#2A2D38] rounded-lg overflow-hidden" data-testid={`section-${id}`}>
    <div className="px-4 py-2.5 border-b border-[#2A2D38] flex items-center gap-2">
      <Icon className={`w-4 h-4 ${color}`} />
      <h3 className="text-xs font-semibold text-white uppercase tracking-wide">{title}</h3>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const StatusPills = ({ data, colors }) => (
  <div className="flex flex-wrap gap-1.5">
    {Object.entries(data || {}).filter(([_, v]) => v > 0).map(([k, v]) => (
      <span key={k} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${colors[k] || "#6B7280"}15`, color: colors[k] || "#6B7280" }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colors[k] || "#6B7280" }} />
        {k.replace(/_/g, " ")} <span className="font-semibold">{v}</span>
      </span>
    ))}
  </div>
);

const ComplianceDashboardPage = () => {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const [posture, setPosture] = useState(null);
  const [moduleHealth, setModuleHealth] = useState(null);
  const [vendorRates, setVendorRates] = useState(null);
  const [redactionRates, setRedactionRates] = useState(null);
  const [disclosureRates, setDisclosureRates] = useState(null);
  const [packageActivity, setPackageActivity] = useState(null);
  const [escalationPatterns, setEscalationPatterns] = useState(null);
  const [recurrenceSummary, setRecurrenceSummary] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, mh, vr, rr, dr, pa, ep, rs, af] = await Promise.all([
        ax.get(`${API}/compliance-dashboard/posture`).then(r => r.data).catch(() => null),
        ax.get(`${API}/compliance-dashboard/module-health`).then(r => r.data).catch(() => null),
        ax.get(`${API}/compliance-dashboard/vendor-rates`).then(r => r.data).catch(() => null),
        ax.get(`${API}/compliance-dashboard/redaction-rates`).then(r => r.data).catch(() => null),
        ax.get(`${API}/compliance-dashboard/disclosure-rates`).then(r => r.data).catch(() => null),
        ax.get(`${API}/compliance-dashboard/package-activity`).then(r => r.data).catch(() => null),
        ax.get(`${API}/compliance-dashboard/escalation-patterns`).then(r => r.data).catch(() => null),
        ax.get(`${API}/compliance-dashboard/recurrence-summary`).then(r => r.data).catch(() => null),
        ax.get(`${API}/compliance-dashboard/activity-feed?limit=15`).then(r => r.data).catch(() => ({ activities: [] })),
      ]);
      setPosture(p); setModuleHealth(mh); setVendorRates(vr);
      setRedactionRates(rr); setDisclosureRates(dr); setPackageActivity(pa);
      setEscalationPatterns(ep); setRecurrenceSummary(rs);
      setActivityFeed(af.activities || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (loading) {
    return (
      <QMSLayout title={t('dashboard:compliance.title')}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#36A9E1] animate-spin" />
        </div>
      </QMSLayout>
    );
  }

  const ps = posture?.posture_score ?? 0;
  const pl = posture?.posture_level ?? "moderate";
  const sm = posture?.summary || {};
  const radialData = [{ name: "Posture", value: ps, fill: POSTURE_COLORS[pl] }];

  return (
    <QMSLayout title={t('dashboard:compliance.title')}>
      <div className="space-y-5" data-testid="compliance-dashboard-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#36A9E1]" />
              {t('compliance.title')}
            </h1>
            <p className="text-sm text-[#8B95A5] mt-0.5">{t('compliance.subtitle')}</p>
          </div>
          <button onClick={loadAll} className="p-2 rounded-lg bg-[#181B23] text-[#8B95A5] hover:text-[#36A9E1] transition-colors" data-testid="refresh-dashboard">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* ── Posture Score + KPIs ── */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="bg-[#1F222D] border border-[#2A2D38] rounded-lg p-4 flex flex-col items-center justify-center" data-testid="posture-score-card">
            <ResponsiveContainer width={140} height={140}>
              <RadialBarChart cx="50%" cy="50%" innerRadius="70%" outerRadius="100%" startAngle={90} endAngle={-270} data={radialData} barSize={12}>
                <RadialBar dataKey="value" cornerRadius={6} background={{ fill: "#2A2D38" }} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="text-center -mt-[85px] mb-6">
              <div className="text-3xl font-bold" style={{ color: POSTURE_COLORS[pl] }}>{ps}</div>
              <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: POSTURE_COLORS[pl] }}>{POSTURE_LABELS[pl]}</div>
            </div>
          </div>
          <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniCard label={t('complianceDash.totalFindings')} value={sm.total_findings} icon={AlertTriangle} color="text-amber-400" />
            <MiniCard label={t('complianceDash.critical')} value={sm.critical_findings} icon={AlertTriangle} color="text-red-400" />
            <MiniCard label={t('complianceDash.openScars')} value={sm.open_scars} icon={Target} color="text-orange-400" />
            <MiniCard label={t('complianceDash.activeAudits')} value={sm.total_audits} icon={Shield} color="text-[#36A9E1]" />
            <MiniCard label={t('complianceDash.vendors')} value={sm.total_vendors} icon={Building2} color="text-emerald-400" sub={`${sm.approved_vendors || 0} ${t('complianceDash.approved')}`} />
            <MiniCard label={t('complianceDash.redactionJobs')} value={sm.total_redaction_jobs} icon={Eye} color="text-red-400" sub={`${sm.completed_redaction_jobs || 0} ${t('complianceDash.completed')}`} />
            <MiniCard label={t('complianceDash.disclosures')} value={sm.total_disclosure_acceptances} icon={FileText} color="text-amber-400" />
            <MiniCard label={t('complianceDash.packages')} value={sm.total_packages} icon={Package} color="text-purple-400" sub={`${sm.total_transfers || 0} ${t('complianceDash.transfers')}`} />
          </div>
        </div>

        {/* ── Risk Factors ── */}
        {(posture?.risk_factors || []).length > 0 && (
          <Section title={t('compliance.activeRiskFactors')} icon={AlertTriangle} color="text-red-400" id="risk-factors">
            <div className="space-y-2">
              {posture.risk_factors.map((rf, i) => (
                <div key={i} className="flex items-center justify-between bg-[#181B23] rounded-lg px-3 py-2" data-testid={`risk-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: SEVERITY_COLORS[rf.severity] }} />
                    <span className="text-sm text-white">{rf.factor}</span>
                    <span className="text-xs text-[#8B95A5]">({rf.count})</span>
                  </div>
                  <span className="text-sm font-mono font-bold text-red-400">{rf.impact}</span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* ── Two-column: Vendor + Redaction ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Section title={t('compliance.vendorRates')} icon={Building2} color="text-emerald-400" id="vendor-rates">
            {vendorRates ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="text-2xl font-bold text-emerald-400">{vendorRates.approval_rate}%</div>
                  <div className="text-[10px] text-[#8B95A5]">approval rate ({vendorRates.reviewed_total} reviewed)</div>
                </div>
                <StatusPills data={vendorRates.by_status} colors={STATUS_COLORS} />
                {Object.keys(vendorRates.by_risk || {}).length > 0 && (
                  <div className="mt-2">
                    <div className="text-[10px] text-[#8B95A5] mb-1.5 uppercase tracking-wide">Risk Profile</div>
                    <ResponsiveContainer width="100%" height={120}>
                      <BarChart data={Object.entries(vendorRates.by_risk).map(([k, v]) => ({ risk: k, count: v }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2A2D38" />
                        <XAxis dataKey="risk" tick={{ fill: "#8B95A5", fontSize: 10 }} />
                        <YAxis tick={{ fill: "#8B95A5", fontSize: 10 }} />
                        <Tooltip content={<Tip />} />
                        <Bar dataKey="count" name={t('complianceDash.vendorsLabel')} radius={[3, 3, 0, 0]}>
                          {Object.entries(vendorRates.by_risk).map(([k], i) => (
                            <Cell key={i} fill={SEVERITY_COLORS[k] || "#6B7280"} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            ) : <Empty />}
          </Section>

          <Section title={t('compliance.redactionCompliance')} icon={Eye} color="text-red-400" id="redaction-rates">
            {redactionRates ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="text-2xl font-bold text-[#36A9E1]">{redactionRates.completion_rate}%</div>
                  <div className="text-[10px] text-[#8B95A5]">completion rate ({redactionRates.total_jobs} jobs)</div>
                </div>
                <StatusPills data={redactionRates.by_status} colors={STATUS_COLORS} />
                {(redactionRates.entity_distribution || []).length > 0 && (
                  <div className="mt-2">
                    <div className="text-[10px] text-[#8B95A5] mb-1.5 uppercase tracking-wide">Entity Types Detected</div>
                    <ResponsiveContainer width="100%" height={120}>
                      <BarChart data={redactionRates.entity_distribution.slice(0, 6)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#2A2D38" />
                        <XAxis type="number" tick={{ fill: "#8B95A5", fontSize: 10 }} />
                        <YAxis dataKey="type" type="category" tick={{ fill: "#8B95A5", fontSize: 9 }} width={90} />
                        <Tooltip content={<Tip />} />
                        <Bar dataKey="count" fill="#EF4444" name={t('complianceDash.detected')} radius={[0, 3, 3, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            ) : <Empty />}
          </Section>
        </div>

        {/* ── Two-column: Disclosures + Packages ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Section title={t('compliance.disclosureAcceptance')} icon={FileText} color="text-amber-400" id="disclosure-rates">
            {disclosureRates ? (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-[#181B23] rounded p-2.5 text-center">
                    <div className="text-lg font-bold text-white">{disclosureRates.total_templates}</div>
                    <div className="text-[9px] text-[#8B95A5]">Templates</div>
                  </div>
                  <div className="bg-[#181B23] rounded p-2.5 text-center">
                    <div className="text-lg font-bold text-emerald-400">{disclosureRates.active_templates}</div>
                    <div className="text-[9px] text-[#8B95A5]">Active</div>
                  </div>
                  <div className="bg-[#181B23] rounded p-2.5 text-center">
                    <div className="text-lg font-bold text-[#36A9E1]">{disclosureRates.total_acceptances}</div>
                    <div className="text-[9px] text-[#8B95A5]">Acceptances</div>
                  </div>
                </div>
                {Object.keys(disclosureRates.by_workflow || {}).length > 0 && (
                  <div>
                    <div className="text-[10px] text-[#8B95A5] mb-1.5 uppercase tracking-wide">By Workflow</div>
                    <StatusPills data={disclosureRates.by_workflow} colors={{ field_audit: "#36A9E1", certification: "#22C55E", regulatory_submission: "#A855F7", training: "#F59E0B" }} />
                  </div>
                )}
              </div>
            ) : <Empty />}
          </Section>

          <Section title={t('compliance.packageActivity')} icon={Package} color="text-purple-400" id="package-activity">
            {packageActivity ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-[#181B23] rounded p-2.5 text-center">
                    <div className="text-lg font-bold text-purple-400">{packageActivity.total_packages}</div>
                    <div className="text-[9px] text-[#8B95A5]">Packages</div>
                  </div>
                  <div className="bg-[#181B23] rounded p-2.5 text-center">
                    <div className="text-lg font-bold text-[#36A9E1]">{packageActivity.total_transfers}</div>
                    <div className="text-[9px] text-[#8B95A5]">Transfers</div>
                  </div>
                </div>
                <StatusPills data={packageActivity.packages_by_status} colors={STATUS_COLORS} />
                {Object.keys(packageActivity.transfers_by_method || {}).length > 0 && (
                  <div>
                    <div className="text-[10px] text-[#8B95A5] mb-1.5 uppercase tracking-wide">Transfer Methods</div>
                    <StatusPills data={packageActivity.transfers_by_method} colors={{ email: "#36A9E1", portal: "#22C55E", physical: "#F59E0B", api: "#A855F7" }} />
                  </div>
                )}
              </div>
            ) : <Empty />}
          </Section>
        </div>

        {/* ── Two-column: Escalation + Recurrence ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Section title={t('complianceDash.escalationPatterns')} icon={Zap} color="text-red-400" id="escalation-patterns">
            {escalationPatterns ? (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-[#181B23] rounded p-2.5 text-center">
                    <div className="text-lg font-bold text-white">{escalationPatterns.total_rules}</div>
                    <div className="text-[9px] text-[#8B95A5]">Rules ({escalationPatterns.enabled_rules} on)</div>
                  </div>
                  <div className="bg-[#181B23] rounded p-2.5 text-center">
                    <div className="text-lg font-bold text-red-400">{escalationPatterns.open_events}</div>
                    <div className="text-[9px] text-[#8B95A5]">Open Events</div>
                  </div>
                  <div className="bg-[#181B23] rounded p-2.5 text-center">
                    <div className="text-lg font-bold text-[#36A9E1]">{escalationPatterns.total_evaluations}</div>
                    <div className="text-[9px] text-[#8B95A5]">Evaluations</div>
                  </div>
                </div>
                {Object.keys(escalationPatterns.events_by_severity || {}).length > 0 && (
                  <StatusPills data={escalationPatterns.events_by_severity} colors={SEVERITY_COLORS} />
                )}
              </div>
            ) : <Empty />}
          </Section>

          <Section title={t('compliance.recurrenceSummary')} icon={Activity} color="text-purple-400" id="recurrence-summary">
            {recurrenceSummary ? (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-[#181B23] rounded p-2.5 text-center">
                    <div className="text-lg font-bold text-white">{recurrenceSummary.total_signals}</div>
                    <div className="text-[9px] text-[#8B95A5]">Total Signals</div>
                  </div>
                  <div className="bg-[#181B23] rounded p-2.5 text-center">
                    <div className="text-lg font-bold text-purple-400">{recurrenceSummary.active_signals}</div>
                    <div className="text-[9px] text-[#8B95A5]">Active</div>
                  </div>
                  <div className="bg-[#181B23] rounded p-2.5 text-center">
                    <div className="text-lg font-bold text-emerald-400">{recurrenceSummary.prevention_acceptance_rate}%</div>
                    <div className="text-[9px] text-[#8B95A5]">Prevention Rate</div>
                  </div>
                </div>
                {Object.keys(recurrenceSummary.by_severity || {}).length > 0 && (
                  <StatusPills data={recurrenceSummary.by_severity} colors={SEVERITY_COLORS} />
                )}
              </div>
            ) : <Empty />}
          </Section>
        </div>

        {/* ── Module Health Grid ── */}
        <Section title={t('compliance.moduleHealth')} icon={Server} color="text-[#36A9E1]" id="module-health">
          {moduleHealth?.modules?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {moduleHealth.modules.map((m, i) => (
                <div key={i} className="bg-[#181B23] rounded-lg p-3 border border-[#2A2D38]" data-testid={`module-${m.module.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="text-[10px] font-semibold text-white truncate">{m.module}</span>
                  </div>
                  <div className="space-y-0.5">
                    {Object.entries(m.metrics || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between text-[9px]">
                        <span className="text-[#8B95A5] capitalize">{k.replace(/_/g, " ")}</span>
                        <span className="text-white font-semibold">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : <Empty />}
        </Section>

        {/* ── Recent Activity Feed ── */}
        <Section title={t('compliance.recentActivity')} icon={Clock} color="text-[#8B95A5]" id="activity-feed">
          {activityFeed.length > 0 ? (
            <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
              {activityFeed.map((a, i) => (
                <div key={i} className="flex items-center gap-2 py-1.5 px-2 bg-[#181B23] rounded text-[10px]" data-testid={`activity-${i}`}>
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${a.severity === "warning" ? "bg-amber-400" : a.severity === "error" ? "bg-red-400" : "bg-[#36A9E1]"}`} />
                  <span className="text-[#8B95A5] w-[100px] flex-shrink-0 truncate">{a.timestamp ? new Date(a.timestamp).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                  <span className="text-white flex-shrink-0">{a.user_name || a.user_id}</span>
                  <span className="text-[#8B95A5] truncate flex-1">{a.action} — {a.resource_type} {a.resource_id}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[10px] text-[#8B95A5] py-3 text-center">{t('compliance.noActivity')}</div>
          )}
        </Section>
      </div>
    </QMSLayout>
  );
};

const Empty = () => {
  const { t } = useTranslation('dashboard');
  return <div className="text-[10px] text-[#8B95A5] py-3 text-center">{t('compliance.noData')}</div>;
};

export default ComplianceDashboardPage;
